import { fetchAuthSession } from '@aws-amplify/auth'
import axios from 'axios'
import { CheckBox } from 'components/CheckBox/CheckBox'
import { Input } from 'components/Input/Input'
import { JustSpinner } from 'components/Loading/Loading'
import { ISelectValue } from 'constants/globalTypes'
import { useDebounce } from 'hooks/useDebounde'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Flex } from 'styling/GlobalStyles'
import { BodyText, TitleText } from 'styling/Typography'
import { ICheckableListProps } from 'types/checkableList.interface'
import { removeDuplicates } from 'utils/helpers'
import { ListItem, ListScrollArea, ListStatus } from './CheckableList.styled'

const DEFAULT_LIMIT = 10
const DEFAULT_LIST_HEIGHT = '14rem'
const SEARCH_DEBOUNCE_MS = 400

export const CheckableList = ({
  link,
  parserFunction,
  selected,
  onChange,
  header,
  selectedLabelText = 'Selected',
  selectedPlaceholder = 'Nothing selected',
  searchPlaceholder = 'Search...',
  searchEnabled = false,
  additionalParams,
  limit = DEFAULT_LIMIT,
  listMaxHeight = DEFAULT_LIST_HEIGHT,
  emptyMessage = 'No items found',
}: ICheckableListProps) => {
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const debouncedQuery = useDebounce(searchInput, SEARCH_DEBOUNCE_MS)
  const [options, setOptions] = useState<ISelectValue[]>([])
  const [loading, setLoading] = useState(false)

  const selectedSummary = useMemo(
    () => (selected.length ? selected.map((item) => item.label).join(', ') : ''),
    [selected],
  )

  const isChecked = useCallback(
    (value: string | number) => selected.some((item) => item.value === value),
    [selected],
  )

  const toggleItem = (item: ISelectValue) => {
    if (isChecked(item.value)) {
      onChange(selected.filter((s) => s.value !== item.value))
    } else {
      onChange([...selected, item])
    }
  }

  useEffect(() => {
    setPage(1)
    setOptions([])
  }, [debouncedQuery, link, additionalParams])

  useEffect(() => {
    const params = new URLSearchParams()
    params.append('page', page.toString())
    params.append('limit', limit.toString())
    if (debouncedQuery) {
      params.append('q', debouncedQuery)
    }
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.append(
            key,
            value instanceof Date ? value.toISOString() : String(value),
          )
        }
      })
    }

    const fetchData = async () => {
      setLoading(true)
      try {
        const session = await fetchAuthSession()
        const res = await axios.get(
          `${process.env.REACT_APP_API}/${link}?${params.toString()}`,
          { headers: { Authorization: `Bearer ${session.tokens.accessToken}` } },
        )
        const parsed = parserFunction(res.data).filter((item) =>
          item.label.toLowerCase().includes(debouncedQuery.toLowerCase()),
        )
        setOptions((prev) =>
          page === 1 ? removeDuplicates(parsed) : removeDuplicates([...prev, ...parsed]),
        )
      } catch {
        if (page === 1) {
          setOptions([])
        }
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [page, debouncedQuery, link, limit, additionalParams, parserFunction])

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = e.currentTarget
    const atBottom = scrollTop + clientHeight >= scrollHeight - 8
    if (atBottom && !loading) {
      setPage((prev) => prev + 1)
    }
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value)
  }

  return (
    <Flex column gap='0.75rem' width='100%'>
      {header && <TitleText weight='bold'>{header}</TitleText>}

      {searchEnabled && (
        <Input
          nomarg
          name='checkableListSearch'
          placeholder={searchPlaceholder}
          value={searchInput}
          onChange={handleSearch}
        />
      )}

      <ListScrollArea $maxHeight={listMaxHeight} onScroll={handleScroll}>
        {loading && options.length === 0 ? (
          <ListStatus>
            <JustSpinner />
          </ListStatus>
        ) : options.length === 0 ? (
          <ListStatus>
            <BodyText grey>{emptyMessage}</BodyText>
          </ListStatus>
        ) : (
          options.map((item) => (
            <ListItem key={String(item.value)} onClick={() => toggleItem(item)}>
              <CheckBox
                square
                bodyText
                state={isChecked(item.value)}
                title={item.label}
              />
            </ListItem>
          ))
        )}
      </ListScrollArea>

      <Input
        nomarg
        readOnly
        labelText={selectedLabelText}
        value={selectedSummary}
        placeholder={selectedPlaceholder}
      />
    </Flex>
  )
}
